<template>
  <section
    ref="reviewsSection"
   
  >
    <div
      class="grid grid-cols-1 items-center gap-x-2 gap-y-4 lg:grid-cols-2"
    >
      <div class="flex gap-4">
        <THeading
          :level="3"
          tag="h3"
        >
          {{ t('reviews') }}
        </THeading>

        <TRating
          v-if="hasAnyReviewRegular"
          :rating="rvRenterRating"
          :number-of-reviews="rvReviews?.length"
          size="lg"
        />
      </div>

      <div class="flex items-center justify-end justify-self-end mb-4 gap-4 order-3 lg:order-2 lg:mb-0">
        <TSelect
          v-model="sortBy"
          :options="sortByOptions"
        />
      </div>

      <div class="order-2 lg:order-3 lg:col-span-2">
        <ReviewCategories
          :categories="reviewsCategoryRatings"
          can-show-progress-bar
          class="mb-8"
        />
      </div>
    </div>

    <TReviewList :reviews="computedReviews" />

    <div
      v-if="rvReviews && rvReviews.length > LIMIT"
      class="mt-3"
    >
      <TButton
        variant="link"
        @click="showMore"
      >
        {{ expanded ? t('actions.showLess') : t('actions.showMore') }}
      </TButton>
    </div>
  </section>
</template>

<script setup lang="ts">
import { SORT_BY_OPTIONS } from '~/constants'
import type { RvReviewDisplay } from '~/components/_tailwind/review/types'
import type { ReviewCategories } from '~/components/review/types'

const LIMIT = 3

const {
  rvReviews,
  rvRenterRating,
  rvRenterCategoryRatings,
} = useRvDetails()

const { t } = useI18n()

const expanded = ref(false)
const reviewsSection = ref<HTMLElement>()

const sortBy = ref<SORT_BY_OPTIONS>('newest')
const sortByOptions = computed(() => Object.values(SORT_BY_OPTIONS).map((value) => ({
  value,
  label: t(value),
})))

const computedReviews = computed<RvReviewDisplay[]>(() => {
  const limit = expanded.value ? 0 : LIMIT
  const sorted = rvReviews.value?.toSorted((a, b): number => {
    if (sortBy.value === 'newest') {
      return new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime()
    }
    else if (sortBy.value === 'highest') {
      return b.Rating - a.Rating
    }
    else if (sortBy.value === 'lowest') {
      return a.Rating - b.Rating
    }

    return 0
  })
  const filtered = sorted?.filter((_, index) => limit
    ? index < limit
    : true) ?? []

  return filtered.map((review) => ({
    Id: review.Id,
    Content: review.Content ?? '',
    CreatedAt: review.CreatedAt,
    DaysBeforeTrip: review.DaysBeforeTrip,
    Reviewer: review.Reviewer
      ? {
          Id: undefined, // Disable links to the reviewer profile
          FirstName: review.Reviewer.FirstName,
          LastName: review.Reviewer.LastName,
          Avatar: review.Reviewer.Avatar,
          Initials: review.Reviewer.Initials ?? formatNameAsInitials(review.Reviewer.FirstName, review.Reviewer.LastName),
        }
      : undefined,
    Rating: review.Rating,
    OverallRating: review.OverallRating,
    ReviewType: review.ReviewType,
    RVDelivered: review.RVDelivered,
    Reply: review.Reply
      ? {
          Content: review.Reply.Content,
          RepliedAt: review.Reply.RepliedAt,
          Replier: review.Reply.Replier
            ? {
                Id: undefined, // Disable links to the reviewer profile
                FirstName: review.Reply.Replier.FirstName,
                LastName: review.Reply.Replier.LastName,
                Initials: review.Reply.Replier.Initials ?? formatNameAsInitials(review.Reply.Replier.FirstName, review.Reply.Replier.LastName),
                Avatar: review.Reply.Replier.Avatar,
              }
            : undefined,
        }
      : undefined,
    ...(review.Photos != null && { Photos: review.Photos }),
  }))
})

const hasAnyReviewRegular = computed(() => {
  return rvReviews.value ? rvReviews.value.some((review) => review.ReviewType === 'Regular') : false
})

function showMore() {
  expanded.value = !expanded.value

  if (!expanded.value) {
    reviewsSection.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }
}

const reviewsCategoryRatings = computed<ReviewCategories>(() => {
  if (!rvRenterCategoryRatings.value?.length) {
    return {}
  }

  return rvRenterCategoryRatings.value?.reduce((acc, category) => {
    if (category.CategoryType !== 'Unknown') {
      acc[category.CategoryType] = category.Rating
    }
    return acc
  }, {} as ReviewCategories)
})
</script>

<i18n lang="json">
{
  "en": {
    "reviews": "Reviews",
    "ReviewContent": "The host canceled one day before this reservation. This is an automated posting. | The host canceled {amount} days before this reservation. This is an automated posting.",
    "newest": "Newest reviews",
    "highest": "Highest rated reviews",
    "lowest": "Lowest rated reviews"
  },
  "fr": {
    "reviews": "Avis",
    "ReviewContent": "L’hôte a annulé un jour avant cette réservation. Ceci est une notification automatisée. | L’hôte a annulé {amount} jours avant cette réservation. Ceci est une notification automatisée.",
    "newest": "Les notes récentes",
    "highest": "Les meilleures notes",
    "lowest": "Les moins bonnes notes"
  }
}
</i18n>
